import { useState } from "react";
import { Box, Stack, Typography } from "@mui/joy";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { useRecoilState } from "recoil";
import { userAtom } from "recoil/userAtom";
import { userApi } from "api/userApi";
import { theme } from "styles/theme";
import CustomTypography from "components/common/CustomTypography";
import TextareaOnboarding from "components/onboarding/TextareaOnboarding";
import KeyboardAwareBottomButton from "components/onboarding/KeyboardAwareBottomButton";
import FullPageWithHeaderAndProgress from "pages/container/FullPageWithHeaderAndProgress";

// 결제 완료 후 닉네임 제출 페이지 (isNicknameChangeRequired 사용자). 제출 → 홈.
// 온보딩 nickname 스텝과 동일한 컨테이너/레이아웃(FullPageWithHeaderAndProgress).
export default function PartnerNicknameSubmitPage() {
  const navigate = useNavigate();
  const [user, setUser] = useRecoilState(userAtom);
  const [text, setText] = useState<string>("");
  const [isFocused, setIsFocused] = useState(false);
  const [loading, setLoading] = useState(false);

  const { mutate: submit } = useMutation(
    (nickname: string) => userApi.submitNickname(nickname),
    {
      onSuccess: (_, nickname) => {
        setUser((prev) =>
          prev
            ? {
                ...prev,
                nickname,
                isNicknameChangeRequired: false,
                isOnboardingRequired: false,
              }
            : prev
        );
        navigate("/", { replace: true });
      },
      onError: () => setLoading(false),
    }
  );

  const handleSubmit = () => {
    const value = text.trim();
    if (value.length >= 1) {
      setLoading(true);
      submit(value);
    }
  };

  return (
    <FullPageWithHeaderAndProgress
      hideHeader
      hideProgress
      body={
        <Stack spacing={5} sx={{ height: "100%" }}>
          <CustomTypography
            variant="Subtitle"
            fontWeight={500}
            sx={{ color: theme.vars.palette.Content.Strong }}
          >
            코치 선생님이 나를 어떻게 부르면 좋을까요?
          </CustomTypography>

          <Stack
            direction="row"
            justifyContent="center"
            alignItems="center"
            spacing={1}
            sx={{ width: "100%" }}
          >
            <Box
              sx={{
                width: "100px",
                height: "54px",
                backgroundColor: theme.vars.palette.Surface.Forward,
                borderRadius: "12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: isFocused
                  ? `inset 0 0 0 1.333px ${theme.vars.palette.Content.Highlight}`
                  : `inset 0 0 0 0`,
              }}
            >
              <TextareaOnboarding
                initialValue={text}
                handleChange={(newValue: string) => setText(newValue)}
                isFocused={isFocused}
                setIsFocused={setIsFocused}
                emojiDisabled={true}
                readOnly={false}
                textAlign={"center"}
              />
            </Box>
            <Typography
              variant="Body"
              fontWeight={500}
              sx={{ color: theme.vars.palette.Content.Strong }}
            >
              님
            </Typography>
          </Stack>

          <KeyboardAwareBottomButton
            loading={loading}
            disabled={text.trim() === ""}
            onClick={handleSubmit}
            text={"다음"}
            highlightBackground={false}
          />
        </Stack>
      }
    />
  );
}
